/**
 * Gap Detection and Auto-Fill System
 * 
 * Detects enclosed empty spaces between rooms and provides intelligent
 * suggestions to fill these gaps when resizing rooms.
 */

import type { Wall, Point, Room } from '../types';
import { calculateRooms, isPointInsideRoom, getRoomBounds } from './floorPlanUtils';

export interface DetectedGap {
  id: string;
  bounds: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  };
  enclosingWalls: Wall[];
  suggestedDimensions: {
    width: number;
    height: number;
  };
  center: Point;
  area: number;
  confidence: 'high' | 'medium' | 'low';
}

export interface GapFillSuggestion {
  gap: DetectedGap;
  targetRoom: Room;
  proposedWalls: Wall[];
  visualPreview: {
    highlightColor: string;
    message: string;
  };
}

/**
 * Detect enclosed gaps in the floor plan
 */
export const detectEnclosedGaps = (
  walls: Wall[],
  currentFloor: number,
  minGapArea: number = 2 // Minimum 2 square meters
): DetectedGap[] => {
  const gaps: DetectedGap[] = [];
  
  // Calculate all existing rooms
  const existingRooms = calculateRooms(walls, {}, 'geometryUpdate', currentFloor);
  
  // Get overall bounds of the floor plan
  const bounds = getFloorPlanBounds(walls);
  if (!bounds) return gaps;
  
  // Sample points in a grid to find potential gaps
  const gridResolution = 50; // pixels between sample points
  const samplePoints: Point[] = [];
  
  for (let x = bounds.minX; x <= bounds.maxX; x += gridResolution) {
    for (let y = bounds.minY; y <= bounds.maxY; y += gridResolution) {
      samplePoints.push({ x, y });
    }
  }
  
  // Find points that are enclosed by walls but not in any room
  const gapPoints: Point[] = samplePoints.filter(point => {
    // Check if point is inside any existing room
    const inRoom = existingRooms.some(room => isPointInsideRoom(point, room.walls));
    if (inRoom) return false;
    
    // Check if point is enclosed by walls (ray casting)
    return isPointEnclosed(point, walls);
  });
  
  // Cluster gap points into distinct gaps
  const gapClusters = clusterPoints(gapPoints, gridResolution * 2);
  
  // Convert clusters to gap objects
  gapClusters.forEach((cluster, index) => {
    const gapBounds = getPointClusterBounds(cluster);
    const enclosingWalls = findEnclosingWalls(gapBounds, walls);
    
    const width = gapBounds.maxX - gapBounds.minX;
    const height = gapBounds.maxY - gapBounds.minY;
    const area = (width * height) / (100 * 100); // Convert to m²
    
    // Only include gaps larger than minimum area
    if (area < minGapArea) return;
    
    // Calculate confidence based on wall enclosure quality
    const confidence = calculateGapConfidence(enclosingWalls, gapBounds);
    
    gaps.push({
      id: `gap_${index}`,
      bounds: gapBounds,
      enclosingWalls,
      suggestedDimensions: {
        width,
        height
      },
      center: {
        x: (gapBounds.minX + gapBounds.maxX) / 2,
        y: (gapBounds.minY + gapBounds.maxY) / 2
      },
      area,
      confidence
    });
  });
  
  return gaps;
};

/**
 * Find the best gap to fill when resizing a room
 */
export const findGapForResize = (
  resizingRoom: Room,
  dragPosition: Point,
  detectedGaps: DetectedGap[],
  proximityThreshold: number = 100 // pixels
): GapFillSuggestion | null => {
  if (detectedGaps.length === 0) return null;
  
  // Find gaps near the drag position
  const nearbyGaps = detectedGaps.filter(gap => {
    const distance = Math.hypot(
      gap.center.x - dragPosition.x,
      gap.center.y - dragPosition.y
    );
    return distance < proximityThreshold;
  });
  
  if (nearbyGaps.length === 0) return null;
  
  // Find the closest gap
  const closestGap = nearbyGaps.reduce((closest, gap) => {
    const distToClosest = Math.hypot(
      closest.center.x - dragPosition.x,
      closest.center.y - dragPosition.y
    );
    const distToGap = Math.hypot(
      gap.center.x - dragPosition.x,
      gap.center.y - dragPosition.y
    );
    return distToGap < distToClosest ? gap : closest;
  });
  
  // Generate walls to fill the gap
  const proposedWalls = generateGapFillingWalls(closestGap, resizingRoom);
  
  return {
    gap: closestGap,
    targetRoom: resizingRoom,
    proposedWalls,
    visualPreview: {
      highlightColor: '#4CAF50',
      message: `Fill ${closestGap.area.toFixed(1)}m² gap?`
    }
  };
};

/**
 * Generate walls to perfectly fill a detected gap
 */
const generateGapFillingWalls = (gap: DetectedGap, room: Room): Wall[] => {
  const walls: Wall[] = [];
  
  // Create a rectangle that fills the gap
  const corners: Point[] = [
    { x: gap.bounds.minX, y: gap.bounds.minY },
    { x: gap.bounds.maxX, y: gap.bounds.minY },
    { x: gap.bounds.maxX, y: gap.bounds.maxY },
    { x: gap.bounds.minX, y: gap.bounds.maxY }
  ];
  
  for (let i = 0; i < corners.length; i++) {
    const start = corners[i];
    const end = corners[(i + 1) % corners.length];
    
    // Check if this edge is already covered by existing walls
    const isExistingWall = gap.enclosingWalls.some(w =>
      pointsMatch(w.start, start) && pointsMatch(w.end, end) ||
      pointsMatch(w.start, end) && pointsMatch(w.end, start)
    );
    
    if (!isExistingWall) {
      walls.push({
        id: `gap_fill_${Date.now()}_${i}`,
        start,
        end,
        thickness: 10,
        type: 'None' as const,
        floor: room.walls[0]?.floor || 1
      });
    }
  }
  
  return walls;
};

/**
 * Check if a point is enclosed by walls using ray casting
 */
const isPointEnclosed = (point: Point, walls: Wall[]): boolean => {
  // Cast a ray from the point to the right
  let intersections = 0;
  const rayEnd: Point = { x: point.x + 10000, y: point.y };
  
  walls.forEach(wall => {
    if (doRayAndLineIntersect(point, rayEnd, wall.start, wall.end)) {
      intersections++;
    }
  });
  
  // Point is enclosed if ray crosses odd number of walls
  return intersections % 2 === 1;
};

/**
 * Check if ray intersects with line segment
 */
const doRayAndLineIntersect = (
  rayStart: Point,
  rayEnd: Point,
  lineStart: Point,
  lineEnd: Point
): boolean => {
  const x1 = rayStart.x, y1 = rayStart.y;
  const x2 = rayEnd.x, y2 = rayEnd.y;
  const x3 = lineStart.x, y3 = lineStart.y;
  const x4 = lineEnd.x, y4 = lineEnd.y;
  
  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 0.001) return false;
  
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
  
  return t >= 0 && u >= 0 && u <= 1;
};

/**
 * Get bounds of all walls
 */
const getFloorPlanBounds = (walls: Wall[]): {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
} | null => {
  if (walls.length === 0) return null;
  
  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  
  walls.forEach(wall => {
    minX = Math.min(minX, wall.start.x, wall.end.x);
    maxX = Math.max(maxX, wall.start.x, wall.end.x);
    minY = Math.min(minY, wall.start.y, wall.end.y);
    maxY = Math.max(maxY, wall.start.y, wall.end.y);
  });
  
  return { minX, maxX, minY, maxY };
};

/**
 * Cluster nearby points together
 */
const clusterPoints = (points: Point[], maxDistance: number): Point[][] => {
  if (points.length === 0) return [];
  
  const clusters: Point[][] = [];
  const assigned = new Set<number>();
  
  points.forEach((point, index) => {
    if (assigned.has(index)) return;
    
    const cluster: Point[] = [point];
    assigned.add(index);
    
    // Find all nearby points
    points.forEach((other, otherIndex) => {
      if (assigned.has(otherIndex)) return;
      
      const dist = Math.hypot(point.x - other.x, point.y - other.y);
      if (dist <= maxDistance) {
        cluster.push(other);
        assigned.add(otherIndex);
      }
    });
    
    clusters.push(cluster);
  });
  
  return clusters;
};

/**
 * Get bounds of a point cluster
 */
const getPointClusterBounds = (points: Point[]): {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
} => {
  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  
  points.forEach(p => {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  });
  
  return { minX, maxX, minY, maxY };
};

/**
 * Find walls that enclose a gap
 */
const findEnclosingWalls = (
  gapBounds: { minX: number; maxX: number; minY: number; maxY: number },
  walls: Wall[]
): Wall[] => {
  const expandedBounds = {
    minX: gapBounds.minX - 20,
    maxX: gapBounds.maxX + 20,
    minY: gapBounds.minY - 20,
    maxY: gapBounds.maxY + 20
  };
  
  return walls.filter(wall => {
    return (
      wall.start.x >= expandedBounds.minX && wall.start.x <= expandedBounds.maxX &&
      wall.start.y >= expandedBounds.minY && wall.start.y <= expandedBounds.maxY
    ) || (
      wall.end.x >= expandedBounds.minX && wall.end.x <= expandedBounds.maxX &&
      wall.end.y >= expandedBounds.minY && wall.end.y <= expandedBounds.maxY
    );
  });
};

/**
 * Calculate confidence level for gap detection
 */
const calculateGapConfidence = (
  enclosingWalls: Wall[],
  bounds: { minX: number; maxX: number; minY: number; maxY: number }
): 'high' | 'medium' | 'low' => {
  // High confidence if surrounded by 4+ walls
  if (enclosingWalls.length >= 4) return 'high';
  if (enclosingWalls.length >= 2) return 'medium';
  return 'low';
};

/**
 * Check if two points match within tolerance
 */
const pointsMatch = (p1: Point, p2: Point, tolerance: number = 5): boolean => {
  return Math.hypot(p1.x - p2.x, p1.y - p2.y) < tolerance;
};
